import type { TimelineRelease } from "../types/model";
import { formatCount, formatReleaseDate } from "../lib/format";
import { resolveModelTitle } from "../lib/model-presentation";
import { useI18n } from "../hooks/use-i18n";
import { useTagRowOverflow } from "../hooks/use-tag-row-overflow";
import { ReleasePills } from "./ReleasePills";
import { VendorMark } from "./VendorMark";
import { TiltedSpotlightCard } from "./react-bits/TiltedSpotlightCard";
import type { OrganizationIndex } from "../lib/organizations";
import {
  developerAttributionLabel,
  organizationLogoCompanies
} from "../lib/organizations";
import type {
  ReleaseFilters,
  ReleaseTagFilterInteraction,
  ReleaseTagFilterKey
} from "../lib/query-models";

interface ModelCardProps {
  record: TimelineRelease;
  showDate: boolean;
  isNew: boolean;
  keyboardActive: boolean;
  organizations: OrganizationIndex;
  onOpen: (record: TimelineRelease) => void;
  filters: ReleaseFilters;
  onToggleFilter: (
    key: ReleaseTagFilterKey,
    value: string,
    interaction?: ReleaseTagFilterInteraction
  ) => void;
  /**
   * Inside a release group's panel a member card is `"match"` when it meets
   * the current search and filters and `"dim"` when a sibling does but it
   * does not.
   */
  emphasis?: "match" | "dim";
}

export function ModelCard({
  record,
  showDate,
  isNew,
  keyboardActive,
  organizations,
  onOpen,
  filters,
  onToggleFilter,
  emphasis
}: ModelCardProps) {
  const { language, localize, t } = useI18n();
  const view = record.groupView;
  const title = view
    ? view.group.name
    : resolveModelTitle(record.id, record.name);
  const capabilities = useTagRowOverflow(language);
  const developerLabel = developerAttributionLabel(
    record,
    organizations,
    language
  );
  const shellClassName = [
    keyboardActive ? "model-card-shell--keyboard" : "",
    view ? "model-card-shell--group" : "",
    emphasis ? `model-card-shell--${emphasis}` : ""
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <TiltedSpotlightCard dataModelId={record.id} className={shellClassName}>
      <article
        className={view ? "model-card model-card--group" : "model-card"}
        onClick={() => onOpen(record)}
      >
        <button
          type="button"
          className="model-card__heading model-card__open"
          aria-label={`${t(view ? "openGroup" : "openDetails")} ${title}${
            language === "zh" ? "，" : " "
          }${t("by")} ${developerLabel}`}
        >
          <VendorMark
            company={record.company}
            modelName={record.name}
            modelFamily={record.family}
            organizationLogoCompany={organizationLogoCompanies(record.organization.primaryId, organizations)}
          />
          <span className="model-card__title-wrap">
            <strong>{title}</strong>
            {view ? (
              // The developer and the count are apart so that opening the
              // group can carry the developer into the window's byline.
              <small>
                <span className="model-card__developer">{developerLabel}</span>
                <span className="model-card__count">
                  {` · ${formatCount(view.members.length, "model", language)}`}
                </span>
              </small>
            ) : (
              <small>{developerLabel}</small>
            )}
          </span>
          {isNew ? <span className="pill pill--new"><span>{t("new")}</span></span> : null}
        </button>

        {view ? (
          // A group card states no member fact and names no member: its
          // members differ in category, status, openness and capabilities,
          // and each member's own card in the group panel shows them.
          <span className="release-pills release-pills--scrollable card-tag-row">
            <span className="pill pill--group">
              <span>{t("releaseGroup")}</span>
            </span>
          </span>
        ) : (
          <ReleasePills
            record={record}
            scrollable
            interactive
            filters={filters}
            onToggleFilter={onToggleFilter}
          />
        )}

        {view ? null : (
          <span
            ref={capabilities}
            className="capability-row card-tag-row"
            aria-label={t("capabilities")}
            // An overflowing row is a keyboard scroller, which Chrome makes a
            // Tab stop with its default square ring. Its tags are not
            // interactive and the overlay lists them all.
            tabIndex={-1}
          >
            {record.capabilities.map((capability) => (
              <span className="pill pill--capability" key={capability}>
                <span>{localize(capability, "capability")}</span>
              </span>
            ))}
          </span>
        )}

        {showDate ? (
          <span className="model-card__date">
            {formatReleaseDate(record, true, language)}
          </span>
        ) : null}
      </article>
    </TiltedSpotlightCard>
  );
}
